import { useContext, useMemo } from "react";
import { Dayjs } from "dayjs";
import {
  BreadTransaction,
  CategoryType,
  FirestoreDocCategory,
  FirestoreDocCategoryGroup,
} from "breadcommon";
import { TransactionsContext } from "../firebaseio/TransactionsContext";
import { CategoriesContext } from "../firebaseio/CategoriesContext";
import { CategoryGroupsContext } from "../firebaseio/CategoryGroupsContext";
import { PreferencesContext } from "../firebaseio/PreferencesContext";
import { useExchangeRates } from "../firebaseio/ExchangeRatesContext";

export const BREAKDOWN_TYPES = ["expenses", "income", "savings"] as const;
export type BreakdownType = (typeof BREAKDOWN_TYPES)[number];

const CATEGORY_TYPE_BY_BREAKDOWN_TYPE: Record<BreakdownType, CategoryType> = {
  expenses: CategoryType.Expense,
  income: CategoryType.Income,
  savings: CategoryType.Savings,
};

export type BreakdownLevel = "groups" | "categories";

/** Past this many slices, the smallest are combined into one "Other" slice. */
export const MAX_SLICES = 8;
export const OTHER_SLICE_ID = "__other__";

export interface CategoryTotal {
  /** The plain signed sum of the category's transactions. */
  net: number;
  transactions: BreadTransaction[];
}

export interface CategoryBreakdownData {
  /** Every non-Silent category with a reviewed transaction in the range. */
  categoryTotals: Map<string, CategoryTotal>;
  /** Unreviewed transactions in the range, which none of the totals count. */
  unreviewedCount: number;
  currency: string;
}

export interface BreakdownSlice {
  id: string;
  label: string;
  value: number;
  transactions: BreadTransaction[];
  /** The slice's group: itself at the group level. */
  groupId: string;
  groupLabel: string;
}

/** A group and the slices inside it, for charts that show both levels. */
export interface BreakdownGroup {
  id: string;
  label: string;
  value: number;
  slices: BreakdownSlice[];
}

export function useCategoryBreakdownData(
  beginDate: Dayjs,
  endDate: Dayjs,
): CategoryBreakdownData {
  const transactions = useContext(TransactionsContext);
  const categories = useContext(CategoriesContext);
  const preferences = useContext(PreferencesContext);
  const { convertTransactionsToSpecificCurrency } = useExchangeRates();

  const currency = preferences?.web?.default_currency ?? "USD";

  return useMemo(
    () =>
      buildCategoryBreakdownData(
        convertTransactionsToSpecificCurrency(
          Array.from(transactions.values()),
          currency,
        ),
        categories,
        beginDate,
        endDate,
        currency,
      ),
    [
      transactions,
      categories,
      beginDate,
      endDate,
      currency,
      convertTransactionsToSpecificCurrency,
    ],
  );
}

/** The slices for one category type, at the group or category level. */
export function useBreakdownSlices(
  data: CategoryBreakdownData,
  type: BreakdownType,
  level: BreakdownLevel,
): BreakdownSlice[] {
  const categories = useContext(CategoriesContext);
  const categoryGroups = useContext(CategoryGroupsContext);
  return useMemo(
    () => buildBreakdownSlices(data, categories, categoryGroups, type, level),
    [data, categories, categoryGroups, type, level],
  );
}

/**
 * Totals each category's reviewed transactions over the range. Transactions are
 * expected to have already been converted into `currency`.
 *
 * Each total is the plain signed sum of the category's transactions, the same
 * rule Historical trends and budget tracking use. Silent categories (transfers
 * between the user's own accounts) are left out, as are unreviewed
 * transactions, whose category is only tentative; those are counted instead.
 */
export function buildCategoryBreakdownData(
  transactions: BreadTransaction[],
  categories: Map<string, FirestoreDocCategory>,
  beginDate: Dayjs,
  endDate: Dayjs,
  currency: string,
): CategoryBreakdownData {
  const categoryTotals = new Map<string, CategoryTotal>();
  let unreviewedCount = 0;

  transactions.forEach((transaction) => {
    if (transaction.date < beginDate || transaction.date > endDate) return;
    if (!transaction.reviewed) {
      unreviewedCount++;
      return;
    }
    if (!transaction.categoryId) return;
    const category = categories.get(transaction.categoryId);
    if (!category || category.type === CategoryType.Silent) return;

    const total = categoryTotals.get(category.id) ?? {
      net: 0,
      transactions: [],
    };
    total.net += transaction.amount;
    total.transactions.push(transaction);
    categoryTotals.set(category.id, total);
  });

  return { categoryTotals, unreviewedCount, currency };
}

/**
 * Slices for the categories of one type, largest first, every one of them.
 * Expense amounts are negative, so expenses are negated to make spending
 * positive.
 *
 * At the group level, a group's slice totals only its categories of this type.
 * Groups can mix category types, so a group holding both Rent and Salary shows
 * only Rent under expenses.
 *
 * A slice that nets to zero or less (a category whose refunds outweighed its
 * purchases, say) can't be drawn, so it's left out.
 */
export function buildBreakdownSlices(
  data: CategoryBreakdownData,
  categories: Map<string, FirestoreDocCategory>,
  categoryGroups: Map<string, FirestoreDocCategoryGroup>,
  type: BreakdownType,
  level: BreakdownLevel,
): BreakdownSlice[] {
  const categoryType = CATEGORY_TYPE_BY_BREAKDOWN_TYPE[type];
  const sign = type === "expenses" ? -1 : 1;

  const slicesById = new Map<string, BreakdownSlice>();
  data.categoryTotals.forEach((total, categoryId) => {
    const category = categories.get(categoryId);
    if (category?.type !== categoryType) return;

    const groupId = category.category_group_id;
    const groupLabel = categoryGroups.get(groupId)?.name ?? "Ungrouped";
    const id = level === "groups" ? groupId : categoryId;
    const slice = slicesById.get(id) ?? {
      id,
      label:
        level === "groups"
          ? groupLabel
          : category.emoji
            ? `${category.emoji} ${category.name}`
            : category.name,
      value: 0,
      transactions: [],
      groupId,
      groupLabel,
    };
    slice.value += sign * total.net;
    slice.transactions.push(...total.transactions);
    slicesById.set(id, slice);
  });

  return Array.from(slicesById.values())
    .filter((slice) => slice.value > 0)
    .sort(byLargestFirst);
}

function byLargestFirst(
  a: { value: number; label: string },
  b: { value: number; label: string },
): number {
  return b.value - a.value || a.label.localeCompare(b.label);
}

/**
 * Keeps the largest slices and combines the rest into one "Other" slice, so a
 * donut doesn't turn into slivers. Expects slices sorted largest first.
 */
export function combineSmallSlices(slices: BreakdownSlice[]): BreakdownSlice[] {
  if (slices.length <= MAX_SLICES) return slices;

  const kept = slices.slice(0, MAX_SLICES - 1);
  const rest = slices.slice(MAX_SLICES - 1);
  return [
    ...kept,
    {
      id: OTHER_SLICE_ID,
      label: "Other",
      value: rest.reduce((sum, slice) => sum + slice.value, 0),
      transactions: rest.flatMap((slice) => slice.transactions),
      groupId: OTHER_SLICE_ID,
      groupLabel: "Other",
    },
  ];
}

/**
 * Gathers slices into their groups, largest group first, each holding its
 * slices largest first. At the group level, each group holds just itself.
 */
export function groupBreakdownSlices(
  slices: BreakdownSlice[],
): BreakdownGroup[] {
  const groups = new Map<string, BreakdownGroup>();
  slices.forEach((slice) => {
    const group = groups.get(slice.groupId) ?? {
      id: slice.groupId,
      label: slice.groupLabel,
      value: 0,
      slices: [],
    };
    group.value += slice.value;
    group.slices.push(slice);
    groups.set(slice.groupId, group);
  });
  return Array.from(groups.values()).sort(byLargestFirst);
}
